import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import moment from "moment";
import Dialog from "../../components/Dialog";
import ModalCallback from "../../model/ModalCallback";
import ButtonStyles from "../../styles/components/ButtonStyles";
import CheckRadioStyles from "../../styles/components/CheckRadioStyles";
import {
    StyledButtonContainer,
    StyledDiv,
    StyledTitle,
} from "../../styles/pages/Schedule/ModalReportStyles";
import ReportService from "../../service/Docente/ReportService";
import GenericUtils from "../../utils/GenericUtils";
import DialogMessageProps from "../../model/DialogMessageProps";
import DialogMessage from "../../components/DialogMessage";
import { TypeDialogMessageEnum } from "../../model/Enums/TypeDialogMessageEnum";

const { BtnPrimary, DefaultButtonLink } = ButtonStyles;

interface IModalReport {
    open: boolean;
    onClose?: (callback?: ModalCallback) => void;
    info: string;
    initialDate: string;
    visibility?: boolean;
}

type ObjectTeacher = {
    id: string;
    nome: string;
    tipo: string;
};

type ObjectReport = {
    id: string;
    nome: string;
    parametros: ObjectTeacher[];
};

const ModalReport: React.FC<IModalReport> = ({
    open,
    onClose,
    info,
    initialDate,
    visibility,
}) => {
    const { instance } = useMsal();
    const [value, setValue] = useState<string>("");
    const [success, setSuccess] = useState<boolean>(false);
    const [showDialog, setShowDialog] = useState(visibility ?? true);
    const [dataReport, setDataReport] = useState<ObjectReport[]>([]);
    const [selectedReport, setSelectedReport] = useState<any>();
    const gradeHoraria = "Relatório Grade Horária";
    const extratoMensal = "Relatório Extrato Mensal Horas";
    const [dialogMessageProps, setDialogMessageProps] =
        useState<DialogMessageProps>({} as DialogMessageProps);
    const successMessage =
        "Sua requisição está sendo processada. Verifique seu email docente para visualizar o relatório solicitado.";

    const getFormattedDate = (date: Date) => {
        return moment(date).format("YYYY-MM-DD");
    };

    const getFirstAndLastDay = (date: string) => {
        const [day, month, year] = date.split("/").map(Number);
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);
        const singleDataReport = new Date(year, month - 1, day);
        const firstDayFormatted = getFormattedDate(firstDay);
        const lastDayFormatted = getFormattedDate(lastDay);
        const exceptionDate = getFormattedDate(singleDataReport);
        return [firstDayFormatted, lastDayFormatted, exceptionDate];
    };

    const setPostObject = (
        filteredReport: ObjectReport,
        teacher: ObjectTeacher,
        initialReference: ObjectTeacher,
        finalReference: ObjectTeacher
    ) => {
        const date = getFirstAndLastDay(initialDate);
        const extratoReport = filteredReport.nome === extratoMensal;
        const parametros = [
            {
                key: {
                    id: teacher.id,
                },
                value: {
                    type: teacher.tipo,
                    value: [info],
                },
            },
            {
                key: {
                    id: initialReference.id,
                },
                value: {
                    type: initialReference.tipo,
                    value: extratoReport ? date[0] : date[2],
                },
            },
        ];

        if (finalReference) {
            parametros.push({
                key: {
                    id: finalReference.id,
                },
                value: {
                    type: finalReference.tipo,
                    value: date[1],
                },
            });
        }

        return {
            nome: filteredReport.nome,
            relatorio: {
                id: filteredReport.id,
            },
            parametros,
        };
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const reportName = event.target.value;

            const filteredReport = dataReport.filter(
                (item: ObjectReport) => item.nome === reportName
            );
            const teacher = filteredReport[0].parametros.filter(
                (item: ObjectTeacher) => item.nome === "Professor"
            );
            const initialReference = filteredReport[0].parametros.filter(
                (item: ObjectTeacher) => item.nome === "Referência Início"
            );
            const finalReference = filteredReport[0].parametros.filter(
                (item: ObjectTeacher) => item.nome === "Referência Fim"
            );

            const postObject = setPostObject(
                filteredReport[0],
                teacher[0],
                initialReference[0],
                finalReference[0]
            );
            setSelectedReport(postObject);

            if (reportName === gradeHoraria) {
                setValue(gradeHoraria);
            }
            if (reportName === extratoMensal) {
                setValue(extratoMensal);
            }
        }
    };

    const handleCloseDialogMessage = () => {
        setDialogMessageProps({} as DialogMessageProps);
        onClose?.();
    };

    const typeDialogMessage = (typeIcon: any) => {
        if (success) {
            return TypeDialogMessageEnum.Success;
        }
        return typeIcon;
    };

    const textMessage = (message: any) => {
        if (success) {
            return successMessage;
        }
        if (message && message.length) {
            return message;
        }

        return "Ocorreu um erro ao processar sua solicitação. Verifique se todos os campos estão preenchidos corretamente e tente novamente. Se o problema persistir, entre em contato com o administrador do sistema.";
    };

    const dialogMessage = dialogMessageProps.show && (
        <DialogMessage
            type={typeDialogMessage(dialogMessageProps.type)}
            show={dialogMessageProps.show}
            title={dialogMessageProps.title}
            text={textMessage(dialogMessageProps.text)}
            onClose={dialogMessageProps.onClose}
        />
    );

    const handleSubmit = async () => {
        const response: any = await ReportService.postReport(
            instance,
            selectedReport
        );
        if (GenericUtils.isSuccessStatus(response)) {
            setSuccess(true);
        }
        const dialog = {
            ...GenericUtils.getPropsDialogError(response),
            onClose: handleCloseDialogMessage,
        };
        setShowDialog(false);
        setDialogMessageProps(dialog);
    };

    const dialogClass = `${showDialog ? "visible-dialog" : "hidden-dialog"}`;

    const getDataReport = async () => {
        const response = await ReportService.getReportData(instance);

        if (GenericUtils.isSuccessStatus(response)) {
            const responseData = response.data.itens;
            setDataReport(responseData);
        }
    };

    useEffect(() => {
        getDataReport();
    }, []);

    return (
        <Dialog
            openModal={open}
            onHide={onClose}
            size="600px"
            className={dialogClass}
        >
            <StyledTitle>Gerar Relatório</StyledTitle>
            <StyledDiv>
                <CheckRadioStyles.StyledFormCheck
                    inline
                    label="Grade Horária"
                    name="group1"
                    type="radio"
                    id="inline-1"
                    value={gradeHoraria}
                    onChange={handleChange}
                />
                <CheckRadioStyles.StyledFormCheck
                    inline
                    label="Extrato Mensal de Atividades"
                    name="group1"
                    type="radio"
                    id="inline-2"
                    value={extratoMensal}
                    onChange={handleChange}
                />
            </StyledDiv>
            <StyledButtonContainer>
                <BtnPrimary disabled={!value} onClick={() => handleSubmit()}>
                    Gerar Relatório
                </BtnPrimary>
                <DefaultButtonLink onClick={onClose}>
                    Cancelar
                </DefaultButtonLink>
            </StyledButtonContainer>
            {dialogMessage}
        </Dialog>
    );
};

export default ModalReport;
